import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  X,
  ChefHat,
  Truck,
  CheckCircle,
  Trash2,
  Eye,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import api from "./axiosConfig";
import { initMerchantSocket } from "./socketClient";
import { getMerchantId } from "./auth";
import { useLanguage  } from "./LanguageContext";

const ORDERS_PER_PAGE = 50;

export default function MerchantOrders() {
  const navigate = useNavigate();
  const { t } = useLanguage();

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    fetchOrders();
    
    const merchantId = getMerchantId();
    const socket = initMerchantSocket(merchantId);

    socket.on("new_order", (order) => {
      setOrders((prev) => [order, ...prev]);
      setCurrentPage(1);
    });
    
    socket.on("order_updated", (order) => {
      setOrders((prev) =>
        prev.map((o) => (o._id === order._id ? order : o))
      );
    });

    return () => {
      socket.off("new_order");
      socket.off("order_updated");
    };
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const fetchOrders = async () => {
    try {
      const res = await api.get("/orders/merchant");
      setOrders(res.data.orders || []);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (orderId, newStatus) => {
    const order = orders.find((o) => o._id === orderId);
    if (!order || order.status === "livree") return;

    try {
      await api.put(`/orders/${orderId}/status`, { status: newStatus });
    } catch (err) {
      console.error(err);
    }
  };

  const handleClearOrders = async () => {
    const confirmed = window.confirm(t('confirm_clear_all'));
    if (!confirmed) return;

    await api.delete("/orders");
    setOrders([]);
    setCurrentPage(1);
  };

const filteredOrders = useMemo(() => {
  const term = searchTerm.toLowerCase().trim();
  if (!term) return orders;

  return orders.filter((o) => {
    return (
      o.customerPhone?.toLowerCase().includes(term) ||
      o._id?.toLowerCase().includes(term) ||
      o.orderNumber?.toLowerCase().includes(term)
    );
  });
}, [orders, searchTerm]);

const totalPages = Math.ceil(filteredOrders.length / ORDERS_PER_PAGE);

const paginatedOrders = useMemo(() => {
  const start = (currentPage - 1) * ORDERS_PER_PAGE;
  return filteredOrders.slice(start, start + ORDERS_PER_PAGE);
}, [filteredOrders, currentPage]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#F5F7FA]">
        {t('loading_orders')}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F7FA] p-8 font-sans text-slate-800">
      {/* HEADER */}
      <div className="flex items-center justify-between mx-auto mb-6 max-w-7xl">
        <div>
          <h1 className="text-3xl font-extrabold">{t('orders')}</h1>
          <p className="text-sm text-gray-500">
            {filteredOrders.length} {t('order_unit')} • {t('page')} {currentPage}/{totalPages || 1}
          </p>
        </div>

        <div className="flex gap-3">
          <button
            onClick={handleClearOrders}
            className="flex items-center gap-2 px-4 py-2 text-xs font-bold text-red-700 bg-red-100 hover:bg-red-200 rounded-xl"
          >
            <Trash2 size={14} />
            {t('clear')}
          </button>

          <button
            onClick={() => navigate("/merchant/dashboard")}
            className="flex items-center gap-2 bg-[#FF3B30] text-white px-5 py-2.5 rounded-xl font-bold text-sm"
          >
            <ArrowLeft size={18} />
            {t('dashboard')}
          </button>
        </div>
      </div>
      {/* 🔍 RECHERCHE COMMANDES */}
      <div className="mx-auto mb-4 max-w-7xl">
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder={t('search_placeholder')}
          className="w-full px-4 py-3 text-sm bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-100"
        />
      </div>
      
      {/* TABLE */}
      <div className="mx-auto overflow-hidden bg-white border max-w-7xl rounded-2xl">
        {paginatedOrders.length === 0 ? (
          <div className="p-12 text-center text-gray-400">
            {t('no_orders_page')}
          </div>
        ) : (
          <table className="w-full">
            <tbody>
              {paginatedOrders.map((o) => {
                const isLocked = o.status === "livree";

                return (
                  <tr key={o._id} className="border-b hover:bg-red-50/30">
                    <td className="p-4 font-mono text-xs">#{o.orderNumber}</td>

                    <td className="p-4">
                      <div className="font-semibold">
                        {o.customerPhone || t('no_phone')}
                      </div>
                      <div className="text-xs text-gray-400">{o.address}</div>
                    </td>

                    <td className="p-4 font-bold">{(o.totalAmount ?? o.total ?? o.subtotal ?? 0)} MRU</td>

                    <td className="p-4">
                      <StatusBadge status={o.status} t={t} />
                    </td>

                    <td className="p-4 text-xs text-gray-500">
                      {new Date(o.createdAt).toLocaleString("fr-FR")}
                    </td>

                    <td className="flex justify-end gap-2 p-4">
                      <ActionButton
                        color="amber"
                        icon={<ChefHat size={14} />}
                        label={t('btn_prepa')}
                        disabled={isLocked}
                        onClick={() =>
                          handleStatusUpdate(o._id, "en_preparation")
                        }
                      />
                      <ActionButton
                        color="indigo"
                        icon={<CheckCircle size={14} />}
                        label={t('btn_ready')}
                        disabled={isLocked}
                        onClick={() => handleStatusUpdate(o._id, "prete")}
                      />
                      <ActionButton
                        color="green"
                        icon={<Truck size={14} />}
                        label={t('btn_sent')}
                        disabled={isLocked}
                        onClick={() => handleStatusUpdate(o._id, "livree")}
                      />
                      <ActionButton
                        color="red"
                        icon={<X size={14} />}
                        label={t('btn_cancel')}
                        disabled={isLocked}
                        onClick={() => handleStatusUpdate(o._id, "annulee")}
                      />
                      <ActionButton
                        color="gray"
                        icon={<Eye size={14} />}
                        label={t('btn_details')}
                        onClick={() =>
                          navigate(`/merchant/orders/${o._id}`)
                        }
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* PAGINATION CONTROLS */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-4 mt-6">
          <button
            disabled={currentPage === 1}
            onClick={() => setCurrentPage((p) => p - 1)}
            className="flex items-center gap-1 px-3 py-2 text-sm font-bold bg-white border rounded-lg disabled:opacity-40"
          >
            <ChevronLeft size={16} />
            {t('prev')}
          </button>

          <span className="text-sm font-semibold">
            {t('page')} {currentPage} / {totalPages}
          </span>

          <button
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage((p) => p + 1)}
            className="flex items-center gap-1 px-3 py-2 text-sm font-bold bg-white border rounded-lg disabled:opacity-40"
          >
            {t('next')}
            <ChevronRight size={16} />
          </button>
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status, t }) {
  const ORDER_STATUS_UI = {
    en_attente: { label: t('st_pending'), className: "bg-amber-100 text-amber-700" },
    en_preparation: { label: t('st_preparing'), className: "bg-yellow-100 text-yellow-800" },
    prete: { label: t('st_ready'), className: "bg-indigo-100 text-indigo-700" },
    livree: { label: t('st_sent'), className: "bg-emerald-100 text-emerald-700" },
    annulee: { label: t('st_canceled'), className: "bg-red-100 text-red-700" },
  };
  const ui = ORDER_STATUS_UI[status];
  return (
    <span className={`px-3 py-1 text-xs font-bold rounded-full ${ui.className}`}>
      {ui.label}
    </span>
  );
}

function ActionButton({ color, icon, label, onClick, disabled }) {
  const colors = {
    amber: "bg-amber-100 text-amber-700",
    indigo: "bg-indigo-100 text-indigo-700",
    green: "bg-emerald-100 text-emerald-700",
    red: "bg-red-100 text-red-700",
    gray: "bg-gray-100 text-gray-700",
  };

  return (
    <button
      disabled={disabled}
      onClick={onClick}
      className={`flex items-center gap-1 px-3 py-2 rounded-lg text-xs font-bold
        ${colors[color]}
        ${disabled ? "opacity-40 cursor-not-allowed" : "hover:opacity-80"}
      `}
    >
      {icon}
      {label}
    </button>
  );
}
